import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Search,
  Users,
} from 'lucide-react'
import { groupRosterQueryOptions, refreshRoster } from '@/utils/rosterQueryOptions'
import { hasOverlays, type RosterMember } from '@/types/rosterTypes'
import { RosterList } from './RosterList'
import { useShellStore } from '@/stores/shellStore'
import { useIsMobile } from '@/hooks/useMediaQuery'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * One group's roster: who a text sent to this group actually reaches, why each
 * person is on the list, and who was taken off it.
 *
 * The counts are the point. A dynamic group in Entra shows its rule-derived
 * membership and nothing else — not the ".Add" people it misses, not the
 * ".Exclude" people it wrongly catches, and not the ones with no mobile number
 * who will be silently skipped at send time. All three are visible here.
 */
export function GroupRosterView({ groupId }: { groupId: string }) {
  const isMobile = useIsMobile()
  const backToList = useShellStore((s) => s.backToList)
  const queryClient = useQueryClient()

  const { data: roster, isPending, error } = useQuery(groupRosterQueryOptions(groupId))

  const [query, setQuery] = useState('')
  const [onlyMissing, setOnlyMissing] = useState(false)
  const [showExcluded, setShowExcluded] = useState(false)

  const refresh = useMutation({
    mutationFn: () => refreshRoster(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groupRoster', groupId] })
      queryClient.invalidateQueries({ queryKey: ['groupRosters'] })
      // The composer resolves this group through its own endpoint. Without
      // this it would keep showing the old count beside a freshly refreshed
      // roster, which is exactly the disagreement this screen exists to end.
      queryClient.invalidateQueries({ queryKey: ['groupMembers', groupId] })
    },
  })

  const members = useMemo(
    () => filterMembers(roster?.members ?? [], query, onlyMissing),
    [roster?.members, query, onlyMissing],
  )

  if (isPending) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <RosterHeader title="Loading group…" isMobile={isMobile} onBack={backToList} />
        <div className="flex-1 space-y-2 overflow-y-auto p-[22px]">
          {[0, 1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-14 w-full rounded-[11px]" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !roster) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <RosterHeader title="Group" isMobile={isMobile} onBack={backToList} />
        <div className="px-6 py-10 text-center text-[13px] text-destructive">
          Couldn't load this group from Microsoft 365.
        </div>
      </div>
    )
  }

  const { counts } = roster
  const overridden = hasOverlays(roster)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <RosterHeader
        title={roster.displayName || 'Group'}
        subtitle={roster.description || (roster.isDynamic ? 'Rule-based Azure AD group' : 'Azure AD group')}
        isMobile={isMobile}
        onBack={backToList}
        onRefresh={() => refresh.mutate()}
        refreshing={refresh.isPending}
      />

      <div className="flex-1 overflow-y-auto p-[22px]">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-4">
          {/* What a text to this group actually does. */}
          <div className="rounded-xl border-[1.5px] border-border bg-surface p-4 shadow-wha-sm">
            <div className="flex items-baseline gap-2">
              <span className="text-[26px] font-extrabold leading-none tracking-[-0.02em] text-foreground">
                {counts.textable}
              </span>
              <span className="text-[13.5px] font-bold text-muted-foreground">
                {counts.textable === 1 ? 'person will' : 'people will'} receive a text sent to this
                group
              </span>
            </div>

            {overridden && (
              <div className="mt-2.5 text-[12.5px] text-muted-foreground">
                <span className="font-bold text-foreground">{counts.baseline}</span> from the rule
                {counts.added > 0 && (
                  <>
                    {' + '}
                    <span className="font-bold text-teal">{counts.added}</span> added
                  </>
                )}
                {counts.excluded > 0 && (
                  <>
                    {' − '}
                    <span className="font-bold text-foreground">{counts.excluded}</span> excluded
                  </>
                )}
                {' = '}
                <span className="font-bold text-foreground">{counts.effective}</span> on the roster
              </div>
            )}

            {counts.unreachable > 0 ? (
              <div className="mt-3 flex items-start gap-2 rounded-[10px] border border-warn-border bg-warn-tint px-3 py-2.5">
                <AlertTriangle className="mt-px h-[15px] w-[15px] flex-none text-warn-ink" />
                <div className="min-w-0 text-[12.5px] text-warn-ink">
                  <span className="font-bold">
                    {counts.unreachable} of the {counts.effective} on this roster{' '}
                    {counts.unreachable === 1 ? 'has' : 'have'} no mobile number on file
                  </span>{' '}
                  and will be skipped. Add their mobile in Microsoft 365, then refresh this page.
                </div>
              </div>
            ) : (
              counts.effective > 0 && (
                <div className="mt-3 text-[12.5px] font-semibold text-sage-ink">
                  Everyone on this roster has a mobile number on file.
                </div>
              )
            )}
          </div>

          {/* Where the overrides are maintained — they're invisible everywhere
              else in this app, so this is the only place to learn the names. */}
          {overridden && (
            <div className="rounded-xl border border-border-2 bg-surface-2 px-3.5 py-3 text-[12.5px] text-muted-foreground">
              <span className="font-bold text-foreground">Membership is overridden.</span>{' '}
              {roster.addGroup && (
                <>
                  People added to this group are held in{' '}
                  <code className="rounded bg-surface px-1 py-px text-[11.5px] font-bold text-foreground">
                    {roster.addGroup.displayName}
                  </code>
                  .{' '}
                </>
              )}
              {roster.excludeGroup && (
                <>
                  Exclusions are held in{' '}
                  <code className="rounded bg-surface px-1 py-px text-[11.5px] font-bold text-foreground">
                    {roster.excludeGroup.displayName}
                  </code>
                  .{' '}
                </>
              )}
              Edit those groups in Microsoft 365 to change who this group reaches.
            </div>
          )}

          {/* Roster */}
          <div className="overflow-hidden rounded-xl border-[1.5px] border-border bg-surface">
            <div className="flex flex-wrap items-center gap-2 border-b border-border-2 bg-surface-2 p-2.5">
              <div className="flex min-w-[160px] flex-1 items-center gap-2 rounded-[9px] border border-border bg-surface px-2.5 py-[7px]">
                <Search className="h-[14px] w-[14px] flex-none text-faint" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Find someone in this group…"
                  className="w-full border-none bg-transparent text-[13px] text-foreground outline-none"
                />
              </div>
              {counts.unreachable > 0 && (
                <button
                  type="button"
                  onClick={() => setOnlyMissing((v) => !v)}
                  className={cn(
                    'inline-flex flex-none items-center gap-1.5 rounded-[9px] border px-2.5 py-[7px] text-[12px] font-bold transition-colors',
                    onlyMissing
                      ? 'border-warn-border bg-warn-tint text-warn-ink'
                      : 'border-border bg-surface text-muted-foreground hover:text-foreground',
                  )}
                >
                  <AlertTriangle className="h-[13px] w-[13px]" />
                  {onlyMissing ? 'Showing missing numbers' : `Missing numbers (${counts.unreachable})`}
                </button>
              )}
            </div>

            <RosterList
              members={members}
              showSource={overridden}
              emptyMessage={
                query || onlyMissing
                  ? 'Nobody here matches that.'
                  : 'This group has no members — a text sent to it would reach nobody.'
              }
            />
          </div>

          {/* Who was taken out. Collapsed: it explains a roster that looks
              short, but it isn't the answer to "who gets this". */}
          {roster.excluded.length > 0 && (
            <div className="overflow-hidden rounded-xl border border-border-2 bg-surface">
              <button
                type="button"
                onClick={() => setShowExcluded((v) => !v)}
                className="flex w-full items-center gap-2 px-3.5 py-3 text-left hover:bg-surface-2"
              >
                {showExcluded ? (
                  <ChevronDown className="h-4 w-4 flex-none text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 flex-none text-muted-foreground" />
                )}
                <span className="text-[13px] font-bold text-foreground">
                  Excluded ({roster.excluded.length})
                </span>
                {roster.excludeGroup && (
                  <span className="truncate text-[12px] text-muted-foreground">
                    · from {roster.excludeGroup.displayName}
                  </span>
                )}
              </button>
              {showExcluded && (
                <RosterList members={roster.excluded} showSource muted className="border-t border-border-2" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function RosterHeader({
  title,
  subtitle,
  isMobile,
  onBack,
  onRefresh,
  refreshing,
}: {
  title: string
  subtitle?: string
  isMobile: boolean
  onBack: () => void
  onRefresh?: () => void
  refreshing?: boolean
}) {
  return (
    <div className="flex min-h-[56px] flex-none items-center gap-2.5 border-b border-border px-[18px] py-[9px]">
      {isMobile && (
        <button type="button" onClick={onBack} className="p-1 text-muted-foreground" aria-label="Back">
          <ChevronLeft className="h-5 w-5" />
        </button>
      )}
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded-[9px] bg-primary-tint text-primary">
        <Users className="h-[17px] w-[17px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-extrabold leading-tight">{title}</div>
        {subtitle && (
          <div className="mt-px truncate text-[11.5px] text-muted-foreground">{subtitle}</div>
        )}
      </div>
      {onRefresh && (
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="inline-flex flex-none items-center gap-1.5 rounded-[9px] border border-border px-2.5 py-[7px] text-[12px] font-bold text-muted-foreground transition-colors hover:text-foreground disabled:opacity-60"
          // Membership is read from Microsoft 365 through a short-lived cache,
          // so somebody who has just filled in a missing number needs a way to
          // see it now rather than concluding their fix didn't work.
          title="Re-read this group from Microsoft 365"
        >
          <RefreshCw className={cn('h-[13px] w-[13px]', refreshing && 'animate-spin')} />
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      )}
    </div>
  )
}

function filterMembers(
  members: Array<RosterMember>,
  query: string,
  onlyMissing: boolean,
): Array<RosterMember> {
  const q = query.trim().toLowerCase()
  return members.filter((m) => {
    if (onlyMissing && m.hasMobile) return false
    if (!q) return true
    return (
      m.displayName.toLowerCase().includes(q) ||
      (m.jobTitle ?? '').toLowerCase().includes(q) ||
      (m.mail ?? '').toLowerCase().includes(q)
    )
  })
}
